/**
 * UserProgress Domain Model
 * Represents a user's progress through a course
 */

export class UserProgress {
  constructor(data) {
    this.userId = data.userId;
    this.courseId = data.courseId;
    this.currentSectionId = data.currentSectionId || null;
    this.sectionProgress = data.sectionProgress || {};
    this.questionProgress = data.questionProgress || {};
    this.overallScore = data.overallScore || 0;
    this.completedSections = data.completedSections || [];
    this.unlockedSections = data.unlockedSections || [];
    this.lastActivityAt = data.lastActivityAt || new Date().toISOString();
    this.createdAt = data.createdAt || new Date().toISOString();
    this.updatedAt = data.updatedAt || new Date().toISOString();
  }

  /**
   * Get progress for a specific section
   */
  getSectionProgress(sectionId) {
    return this.sectionProgress[sectionId] || null;
  }

  /**
   * Get progress for a specific question
   */
  getQuestionProgress(questionId) {
    return this.questionProgress[questionId] || null;
  }

  /**
   * Check if section is unlocked
   */
  isSectionUnlocked(sectionId) {
    return this.unlockedSections.includes(sectionId);
  }

  /**
   * Check if section is completed
   */
  isSectionCompleted(sectionId) {
    return this.completedSections.includes(sectionId);
  }

  /**
   * Update section progress
   */
  updateSectionProgress(sectionId, progressData) {
    this.sectionProgress[sectionId] = {
      ...this.sectionProgress[sectionId],
      ...progressData,
      updatedAt: new Date().toISOString(),
    };
    this.updatedAt = new Date().toISOString();
  }

  /**
   * Update question progress
   */
  updateQuestionProgress(questionId, progressData) {
    this.questionProgress[questionId] = {
      ...this.questionProgress[questionId],
      ...progressData,
      updatedAt: new Date().toISOString(),
    };
    this.updatedAt = new Date().toISOString();
  }

  /**
   * Unlock section (with sequential validation)
   * @param {string} sectionId - Section to unlock
   * @param {Array} allSections - All course sections ordered by index (optional, for validation)
   */
  unlockSection(sectionId, allSections = null) {
    if (!this.unlockedSections.includes(sectionId)) {
      // If we have section data, validate sequential unlocking
      if (allSections && allSections.length > 0) {
        const sectionToUnlock = allSections.find(s => s.id === sectionId);
        if (sectionToUnlock) {
          // Check if all previous sections are unlocked
          const previousSections = allSections.filter(s => s.index < sectionToUnlock.index);
          const allPreviousUnlocked = previousSections.every(s => this.unlockedSections.includes(s.id));
          
          if (!allPreviousUnlocked) {
            console.warn('[UserProgress.unlockSection] Cannot unlock section out of sequence:', {
              sectionId,
              sectionIndex: sectionToUnlock.index,
              unlockedSections: this.unlockedSections,
              previousSections: previousSections.map(s => ({ id: s.id, index: s.index, unlocked: this.unlockedSections.includes(s.id) })),
            });
            return false; // Prevent out-of-order unlocking
          }
        }
      }
      
      this.unlockedSections.push(sectionId);
      this.sectionProgress[sectionId] = {
        ...(this.sectionProgress[sectionId] || {}),
        sectionId,
        unlocked: true,
        lastActivityAt: this.sectionProgress[sectionId]?.lastActivityAt || new Date().toISOString(),
      };
      this.updatedAt = new Date().toISOString();
      return true;
    }

    this.sectionProgress[sectionId] = {
      ...(this.sectionProgress[sectionId] || {}),
      sectionId,
      unlocked: true,
      lastActivityAt: this.sectionProgress[sectionId]?.lastActivityAt || new Date().toISOString(),
    };
    return true; // Already unlocked
  }

  /**
   * Mark section as completed
   */
  completeSection(sectionId) {
    if (!this.completedSections.includes(sectionId)) {
      this.completedSections.push(sectionId);
      this.updatedAt = new Date().toISOString();
    }
  }

  /**
   * Convert to JSON
   */
  toJSON() {
    return {
      userId: this.userId,
      courseId: this.courseId,
      currentSectionId: this.currentSectionId,
      sectionProgress: this.sectionProgress,
      questionProgress: this.questionProgress,
      overallScore: this.overallScore,
      completedSections: this.completedSections,
      unlockedSections: this.unlockedSections,
      lastActivityAt: this.lastActivityAt,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt,
    };
  }

  /**
   * Create from JSON
   */
  static fromJSON(json) {
    return new UserProgress(json);
  }

  /**
   * Repair and validate progress data to ensure sequential section unlocking
   * @param {Array} courseSections - All course sections ordered by index
   * @returns {boolean} - True if repairs were made
   */
  repairSequentialUnlocks(courseSections) {
    if (!courseSections || courseSections.length === 0) return false;

    let repairsMade = false;
    const sortedSections = [...courseSections].sort((a, b) => a.index - b.index);
    const validUnlocks = [];
    
    console.log('[UserProgress.repairSequentialUnlocks] Starting repair:', {
      userId: this.userId,
      courseId: this.courseId,
      currentUnlocks: this.unlockedSections,
    });

    // Check each section in order
    for (const section of sortedSections) {
      const isUnlocked = this.unlockedSections.includes(section.id);
      const previousIndex = section.index - 1;
      
      if (section.index === 0) {
        // First section should always be unlocked
        if (!isUnlocked) {
          console.warn('[UserProgress.repairSequentialUnlocks] First section not unlocked, adding:', section.id);
          validUnlocks.push(section.id);
          repairsMade = true;
        } else {
          validUnlocks.push(section.id);
        }
      } else {
        // For other sections, check if previous section is unlocked
        const previousSection = sortedSections[previousIndex];
        const previousIsValid = validUnlocks.includes(previousSection.id);
        
        if (isUnlocked && !previousIsValid) {
          // This section is unlocked but previous isn't - remove this unlock
          console.warn('[UserProgress.repairSequentialUnlocks] Removing invalid unlock (gap detected):', {
            sectionId: section.id,
            sectionIndex: section.index,
            previousSection: previousSection.id,
            previousUnlocked: previousIsValid,
          });
          repairsMade = true;
          // Don't add to validUnlocks
        } else if (isUnlocked && previousIsValid) {
          // Valid unlock - previous section is unlocked
          validUnlocks.push(section.id);
        }
        // If not unlocked, that's fine - just don't add it
      }
    }

    if (repairsMade) {
      console.log('[UserProgress.repairSequentialUnlocks] Repairs completed:', {
        before: this.unlockedSections,
        after: validUnlocks,
        removed: this.unlockedSections.filter(id => !validUnlocks.includes(id)),
      });
      this.unlockedSections = validUnlocks;
      this.updatedAt = new Date().toISOString();
    }

    // Always synchronize sectionProgress unlock flags with unlockedSections array
    const unlockSet = new Set(this.unlockedSections);
    for (const section of sortedSections) {
      const previous = this.sectionProgress[section.id] || {};
      const shouldBeUnlocked = unlockSet.has(section.id);
      if (previous.unlocked !== shouldBeUnlocked) {
        this.sectionProgress[section.id] = {
          ...previous,
          sectionId: section.id,
          unlocked: shouldBeUnlocked,
          updatedAt: new Date().toISOString(),
        };
        repairsMade = true;
      }
    }

    return repairsMade;
  }
}
